import { ContentTypeName, MasterTranslation, SeoPageType } from '@/constants';
import { useGlobalState } from '@/hooks';
import {
  type ILandingPage,
  type ILeaderboardEntry,
  type ITrackingPageInsights,
  LeadGenBannerVariant,
  type SectionItem,
} from '@/interfaces';
import { applyTrackingStats, capitalize, getPartnerLogos, getTrackingPageInsights } from '@/utils';
import { interpolate } from './interpolate.utils';

export interface TrackingPageInsights {
  insights?: ITrackingPageInsights;
  isCarrierPage: boolean;
  partnerLogos: ILeaderboardEntry[];
  topCarriersTitle: string;
  topMerchantsTitle: string;
  /** The CMS sections to render in place, with this page's own figures in the stats band. */
  contentSections: SectionItem[];
}

/**
 * Everything the SEO page renderer needs to place the tracking-insight sections.
 *
 * The sections are interleaved with ad slots and the FAQ rather than forming one
 * block, so they cannot live in a single wrapper component; this keeps their data and rules in
 * one place and leaves the renderer to lay the page out.
 *
 * @param placeName Localized country / region name, resolved on the server (see
 *   `getSeoPagePlaceName`) — never here, because browsers cannot name UN M.49 regions.
 */
const useTrackingPageInsights = (data: ILandingPage, placeName?: string | null): TrackingPageInsights => {
  const { t } = useGlobalState();
  const { sectionList = [], seoPageType } = data;

  // First-party figures, read from the six JSON fields on the entry. Undefined for any page the
  // Growth sheet has not covered, in which case every insight section simply does not render.
  const insights = getTrackingPageInsights(data);
  const isCarrierPage = seoPageType === SeoPageType.carrier;

  // The partner strip is drawn on the country and region artboards only; a carrier page opens
  // with its own identity block instead. It previews the brands the page ranks further down.
  const partnerLogos =
    seoPageType === SeoPageType.country || seoPageType === SeoPageType.region ? getPartnerLogos(insights) : [];

  // "Top 10 Carriers in Germany", or the generic heading when the page has no place or the
  // translation has no `{place}` slot yet.
  const leaderboardTitle = (withPlace: MasterTranslation, generic: MasterTranslation) =>
    (placeName && interpolate(t(withPlace), 'place', placeName)) || t(generic);

  const cmsSections = sectionList.reduce<SectionItem[]>((kept, section) => {
    const typeName = capitalize(section.__typename) as ContentTypeName;

    if (typeName === ContentTypeName.PageOverviewBanner) return kept;
    kept.push(
      typeName === ContentTypeName.LeadGenBanner ? { ...section, variant: LeadGenBannerVariant.FULL_WIDTH } : section,
    );
    return kept;
  }, []);

  return {
    insights,
    isCarrierPage,
    partnerLogos,
    topCarriersTitle: leaderboardTitle(MasterTranslation.TopCarriersTitle, MasterTranslation.TopCarriersTitleGeneric),
    // A carrier page ranks the merchants that ship with it ("Top 10 Merchants"); country and region
    // pages rank them within the place ("Top Merchants in Vietnam").
    topMerchantsTitle: isCarrierPage
      ? t(MasterTranslation.TopMerchantsTitleCarrier)
      : leaderboardTitle(MasterTranslation.TopMerchantsTitle, MasterTranslation.TopMerchantsTitleGeneric),

    contentSections: applyTrackingStats(cmsSections, insights?.trackingStats, insights?.trackingStatsTitle),
  };
};

export default useTrackingPageInsights;
